import { FormEvent, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PasswordInput from '@/components/feature/PasswordInput';
import { useMemberAuth } from '@/contexts/MemberAuthContext';
import { MemberApiError, getCaptchaImageUrl } from '@/services/memberAuth';
import { getDistrictsByCity, getPostalCode, taiwanCities } from '@/data/taiwanAddress';

const inputClass =
  'w-full px-3.5 md:px-5 py-2.5 md:py-4 rounded-xl border border-[#E8E0D6] text-base outline-none focus:ring-2 focus:ring-[#1B3D2A]/15 focus:border-[#1B3D2A]';
const selectClass = inputClass + ' bg-white';

const currentYear = new Date().getFullYear();
const birthYears = Array.from({ length: 100 }, (_, i) => String(currentYear - i));
const birthMonths = Array.from({ length: 12 }, (_, i) => String(i + 1));
const birthDays = Array.from({ length: 31 }, (_, i) => String(i + 1));

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get('redirect') || '/account';
  const { register } = useMemberAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [gender, setGender] = useState<'Male' | 'Female' | ''>('');
  const [birthYear, setBirthYear] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthDay, setBirthDay] = useState('');
  const [phone, setPhone] = useState('');
  const [addressCity, setAddressCity] = useState('');
  const [addressDistrict, setAddressDistrict] = useState('');
  const [addressLine, setAddressLine] = useState('');
  const addressDistricts = getDistrictsByCity(addressCity);
  const addressPostalCode = getPostalCode(addressCity, addressDistrict);

  function handleAddressCityChange(city: string) {
    setAddressCity(city);
    setAddressDistrict('');
  }
  const [captcha, setCaptcha] = useState('');
  const [captchaUrl, setCaptchaUrl] = useState(getCaptchaImageUrl());
  const [agreeTerms, setAgreeTerms] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState('');
  const [registered, setRegistered] = useState(false);

  function refreshCaptcha() {
    setCaptcha('');
    setCaptchaUrl(getCaptchaImageUrl());
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError('');
    setErrors({});

    if (password !== confirmPassword) {
      setErrors({ ConfirmPassword: '兩次輸入的密碼不一致。' });
      return;
    }
    if (!/^09\d{8}$/.test(phone.trim())) {
      setErrors({ Phone: '請輸入正確的手機號碼格式（09 開頭，共 10 碼）。' });
      return;
    }
    if (!agreeTerms) {
      setErrors({ AgreeTerms: '請詳閱並勾選同意會員條款。' });
      return;
    }

    setSubmitting(true);
    try {
      await register({
        Name: name.trim(),
        Email: email.trim(),
        Password: password,
        Gender: gender || undefined,
        Phone: phone.trim() || undefined,
        BirthYear: birthYear || undefined,
        BirthMonth: birthMonth || undefined,
        BirthDay: birthDay || undefined,
        AddressCity: addressCity || undefined,
        AddressDistrict: addressDistrict || undefined,
        AddressPostalCode: addressPostalCode || undefined,
        AddressLine: addressLine.trim() || undefined,
        Captcha: captcha.trim(),
        AgreeTerms: agreeTerms,
      });
      setRegistered(true);
    } catch (err) {
      if (err instanceof MemberApiError) {
        setFormError(err.message);
        setErrors(err.errors || {});
      } else {
        setFormError('註冊失敗，請稍後再試。');
      }
      refreshCaptcha();
    } finally {
      setSubmitting(false);
    }
  }

  if (registered) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="pt-20 md:pt-24">
          <div className="mx-auto max-w-md px-6 py-16 md:py-24 text-center">
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#1B3D2A]/10">
              <i className="ri-mail-check-line text-3xl text-[#1B3D2A]" />
            </div>
            <h1 className="text-2xl font-bold text-[#1B3D2A]">請至信箱完成驗證</h1>
            <p className="mt-3 text-sm leading-relaxed text-[#5A6B5A]">
              我們已寄出一封驗證信至 <span className="font-bold text-[#1B3D2A]">{email}</span>，請點擊信中連結完成驗證後即可登入。
            </p>
            <button
              type="button"
              onClick={() => navigate(`/login?redirect=${encodeURIComponent(redirectPath)}`)}
              className="mt-6 rounded-xl bg-[#1B3D2A] px-6 py-3 text-sm font-bold text-white hover:bg-[#143026]"
            >
              前往登入
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-20 md:pt-24">
        <div className="mx-auto max-w-2xl px-6 py-12 md:py-20">
          <div className="mb-8 text-center">
            <span className="inline-block text-xs font-semibold tracking-widest text-[#D4A574] uppercase mb-2">
              Join Us
            </span>
            <h1 className="text-2xl md:text-3xl font-bold text-[#1B3D2A]">加入會員</h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5 rounded-2xl border border-[#E8E0D6] bg-[#FAF8F5] p-6 md:p-8">
            <div>
              <label htmlFor="register-email" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">會員帳號（Email）</label>
              <input id="register-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" className={inputClass} />
              {errors.Email && <p className="mt-1 text-xs text-[#c3423f]">{errors.Email}</p>}
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="register-password" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">會員密碼</label>
                <PasswordInput id="register-password" required minLength={8} value={password} onChange={setPassword} placeholder="至少 8 碼" className={inputClass} />
                {errors.Password && <p className="mt-1 text-xs text-[#c3423f]">{errors.Password}</p>}
              </div>
              <div>
                <label htmlFor="register-confirm-password" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">確認密碼</label>
                <PasswordInput id="register-confirm-password" required minLength={8} value={confirmPassword} onChange={setConfirmPassword} placeholder="請再次輸入密碼" className={inputClass} />
                {errors.ConfirmPassword && <p className="mt-1 text-xs text-[#c3423f]">{errors.ConfirmPassword}</p>}
              </div>
            </div>

            <div>
              <label htmlFor="register-name" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">姓名</label>
              <input id="register-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="請輸入姓名" className={inputClass} />
              {errors.Name && <p className="mt-1 text-xs text-[#c3423f]">{errors.Name}</p>}
            </div>

            <div>
              <span className="block text-sm font-medium text-[#1B3D2A] mb-1.5">性別（選填）</span>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm text-[#1B3D2A]">
                  <input type="radio" name="gender" value="Male" checked={gender === 'Male'} onChange={() => setGender('Male')} className="h-4 w-4 accent-[#1B3D2A]" />
                  男
                </label>
                <label className="flex items-center gap-2 text-sm text-[#1B3D2A]">
                  <input type="radio" name="gender" value="Female" checked={gender === 'Female'} onChange={() => setGender('Female')} className="h-4 w-4 accent-[#1B3D2A]" />
                  女
                </label>
              </div>
            </div>

            <div>
              <span className="block text-sm font-medium text-[#1B3D2A] mb-1.5">生日（選填）</span>
              <div className="grid grid-cols-3 gap-3">
                <select value={birthYear} onChange={(e) => setBirthYear(e.target.value)} className={selectClass} aria-label="出生年">
                  <option value="">請選擇</option>
                  {birthYears.map((year) => (
                    <option key={year} value={year}>{year} 年</option>
                  ))}
                </select>
                <select value={birthMonth} onChange={(e) => setBirthMonth(e.target.value)} className={selectClass} aria-label="出生月">
                  <option value="">請選擇</option>
                  {birthMonths.map((month) => (
                    <option key={month} value={month}>{month} 月</option>
                  ))}
                </select>
                <select value={birthDay} onChange={(e) => setBirthDay(e.target.value)} className={selectClass} aria-label="出生日">
                  <option value="">請選擇</option>
                  {birthDays.map((day) => (
                    <option key={day} value={day}>{day} 日</option>
                  ))}
                </select>
              </div>
              {errors.BirthDate && <p className="mt-1 text-xs text-[#c3423f]">{errors.BirthDate}</p>}
            </div>

            <div>
              <label htmlFor="register-phone" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">聯絡電話（手機）</label>
              <input
                id="register-phone"
                type="tel"
                required
                inputMode="numeric"
                pattern="09\d{8}"
                title="請輸入 09 開頭的 10 碼手機號碼"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="0912345678"
                className={inputClass}
              />
              <p className="mt-1 text-xs text-[#9A8A7A]">請輸入 09 開頭的 10 碼手機號碼，做為帳號安全與聯絡用途。</p>
              {errors.Phone && <p className="mt-1 text-xs text-[#c3423f]">{errors.Phone}</p>}
            </div>

            <div>
              <span className="block text-sm font-medium text-[#1B3D2A] mb-1.5">聯絡地址（選填）</span>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1.2fr_1.2fr_1fr]">
                <select value={addressCity} onChange={(e) => handleAddressCityChange(e.target.value)} className={selectClass} aria-label="縣市">
                  <option value="">請選擇縣市</option>
                  {taiwanCities.map((city) => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
                <select
                  value={addressDistrict}
                  onChange={(e) => setAddressDistrict(e.target.value)}
                  className={selectClass}
                  aria-label="鄉鎮市區"
                  disabled={!addressCity}
                >
                  <option value="">{addressCity ? '請選擇鄉鎮市區' : '請先選擇縣市'}</option>
                  {addressDistricts.map((district) => (
                    <option key={district.name} value={district.name}>{district.name}</option>
                  ))}
                </select>
                <input value={addressPostalCode} readOnly placeholder="郵遞區號" className={`${inputClass} bg-[#F1EEE8] text-[#9A8A7A]`} />
              </div>
              <input value={addressLine} onChange={(e) => setAddressLine(e.target.value)} placeholder="詳細地址" className={`${inputClass} mt-3`} />
            </div>

            <div>
              <label htmlFor="register-captcha" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">驗證碼</label>
              <div className="flex items-center gap-3">
                <img src={captchaUrl} alt="驗證碼圖片" className="h-11 rounded-xl border border-[#E8E0D6]" />
                <button
                  type="button"
                  onClick={refreshCaptcha}
                  className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#E8E0D6] text-[#5A6B5A] hover:bg-white"
                  aria-label="重新產生驗證碼"
                >
                  <i className="ri-refresh-line" />
                </button>
                <input
                  id="register-captcha"
                  required
                  value={captcha}
                  onChange={(e) => setCaptcha(e.target.value)}
                  placeholder="請輸入圖片中的驗證碼"
                  className={`${inputClass} flex-1`}
                />
              </div>
              {errors.Captcha && <p className="mt-1 text-xs text-[#c3423f]">{errors.Captcha}</p>}
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm text-[#1B3D2A]">
                <input
                  type="checkbox"
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                  className="h-4 w-4 shrink-0 accent-[#1B3D2A]"
                />
                <span>
                  我已同意並閱讀
                  <a href="/terms" target="_blank" rel="noreferrer" className="mx-1 font-bold text-[#1B3D2A] underline">
                    「會員條款」
                  </a>
                </span>
              </label>
              {errors.AgreeTerms && <p className="mt-1 text-xs text-[#c3423f]">{errors.AgreeTerms}</p>}
            </div>

            {formError && <div className="rounded-xl bg-[#fdebea] px-4 py-3 text-sm text-[#c3423f]">{formError}</div>}

            <button
              type="submit"
              disabled={submitting}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#1B3D2A] py-3.5 text-base font-bold text-white transition-colors hover:bg-[#143026] disabled:opacity-60"
            >
              {submitting ? '註冊中...' : '註冊'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-[#5A6B5A]">
            已經是會員？
            <button type="button" onClick={() => navigate(`/login?redirect=${encodeURIComponent(redirectPath)}`)} className="ml-1 font-bold text-[#1B3D2A] hover:underline">
              前往登入
            </button>
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
